import { take, filter } from 'rxjs/operators';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import * as actions from '../../store/actions';
import {
  getFileUploadProgress,
  getFileUploadResponse,
} from '../../store/file-upload/file-upload-selectors';
import { FileRecordDto } from '@careapp-group/ng-sdk';

@Component({
    selector: 'ca-photo-picker',
    templateUrl: 'photo-picker.component.html',
    styleUrls: ['photo-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhotoPickerItemComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class PhotoPickerItemComponent implements ControlValueAccessor, OnDestroy {
  @ViewChild('fileInput', { static: true })
  file;

  @Input()
  title: string;
  @Input()
  isCircular: boolean = false;

  @Input()
  purpose?: string;

  @Input()
  fit?: string;

  @Input()
  modelName?: string;

  hover: boolean;
  progress: number;
  photo: FileRecordDto;

  private id: string;
  private uploadProgressSub: Subscription;
  private uploadSuccessSub: Subscription;
  // Add a new property to store the temporary preview URL
  public tempPreviewUrl: string | null = null;

  constructor(public store: Store<any>, public cd: ChangeDetectorRef) {}

  changePhoto() {
    this.file.nativeElement.click();
  }

  clearFileInput() {
    this.file.nativeElement.value = '';
  }

  ngOnDestroy() {
    this.clearUploadSubs();
    if (this.id) {
      this.store.dispatch(actions.RequestFileUploadCancelAction({ id: this.id }));
    }
  }

  clearUploadSubs() {
    if (this.uploadProgressSub) {
      this.uploadProgressSub.unsubscribe();
    }
    if (this.uploadSuccessSub) {
      this.uploadSuccessSub.unsubscribe();
    }
  }

  photoChanged(file: File) {
    if (this.id) {
      this.store.dispatch(actions.RequestFileUploadCancelAction({ id: this.id }));
    }

    this.clearUploadSubs();

    this.id = Math.random().toString(36);
    this.store.dispatch(
      actions.RequestFileUploadStartAction({
        id: this.id,
        modelName: this.modelName,
        purpose: this.purpose,
        url: 'upload',
        file,
      })
    );

    this.uploadProgressSub = this.store
      .select(getFileUploadProgress(this.id))
      .subscribe((progress) => {
        this.progress = progress;
        this.cd.detectChanges();
      });

    this.uploadSuccessSub = this.store
      .select(getFileUploadResponse(this.id))
      .pipe(
        filter((res) => !!res && !!res.id),
        take(1)
      )
      .subscribe((success) => {
        this.hover = false;
        this.id = null;
        this.progress = 100;
        this.photo = success;
        this.onChange(success);
        this.clearUploadSubs();
        this.cd.detectChanges();
      });
  }
  processWebImage(event) {
    if (!event.target.files.length) {
      return;
    }
    const [file] = event.target.files;
    if (file) {
      const reader = new FileReader();
      reader.onload = (readerEvent) => {
        const imageData = (readerEvent.target as any).result;
        this.tempPreviewUrl = imageData;
        this.cd.detectChanges();
        this.photoChanged(file);
      };
      reader.readAsDataURL(file);
      this.clearFileInput();
    }
  }
  // Control Value Accessor methods
  public writeValue(data: FileRecordDto) {
    if (data?.id) {
      this.photo = data;
      // Only clear the preview when we get a fully linked FileRecordDto
      // (Otherwise, it won't be able to retrieve a download URL)
      if (data.modelId && data.modelName) {
        this.tempPreviewUrl = null;
      }
    } else {
      this.photo = null;
      this.tempPreviewUrl = null;
    }
    this.cd.detectChanges();
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched() {
    // lint
  }

  private onChange(event) {
    this.propagateChange(event);
  }

  private propagateChange = (_: any) => null;
}
